import {
  Banner,
  Box,
  Button,
  Column,
  Columns,
  ErrorBoundary,
  FlexBox,
  Page,
  Spinner,
} from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { connect } from 'react-redux';
import { Form, InjectedFormProps, reduxForm } from 'redux-form';

import { CustomizationPageSelectors } from '../../common/selectors';
import { DixaMessengerPanelForms } from '../Panel/messengerPanelForms';

import { CustomizationFormView } from './components/FollowupCustomizationForm';
import { CsatPreview } from './components/csatPreview/CsatPreview';
import { formatFormValuesFromCsatPreview } from './formatFormValuesForCsatPreview';
import styles from './index.module.scss';
import { LanguageCustomizationForm } from './types';

export type OwnProps = {
  languageName?: string;
  settingsId?: string;
  mode?: 'create' | 'edit';
};

export type StateProps = {
  formValues: LanguageCustomizationForm.FormValues;
  initialValues: Partial<LanguageCustomizationForm.FormValues>;
  isLoading?: boolean;
};

export type DispatchProps = {
  onSubmit: (customization: LanguageCustomizationForm.FormValues) => void;
};

type InjectedProps = InjectedFormProps<
  LanguageCustomizationForm.FormValues,
  CustomizationPageViewProps
>;

export type CustomizationPageViewProps = OwnProps & StateProps & DispatchProps;

type PropsT = CustomizationPageViewProps & InjectedProps;

const CustomizationPageView: React.FunctionComponent<
  PropsT & InjectedProps
> = ({
  onSubmit,
  formValues,
  languageName,
  handleSubmit,
  error: errorMessage,
  isLoading,
  pristine,
  mode,
}) => {
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const previewIframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    const sendFormValuesOverToPreview = () => {
      previewIframeRef.current?.contentWindow?.postMessage(
        {
          type: 'setCustomizationObject',
          payload: formatFormValuesFromCsatPreview(formValues),
        },
        '*',
      );
    };

    setTimeout(sendFormValuesOverToPreview, previewLoaded ? 0 : 1000);
  }, [formValues, previewLoaded]);

  const handlePreviewLoaded = useCallback(() => {
    if (!previewLoaded) {
      setTimeout(() => setPreviewLoaded(true));
    }
  }, [previewLoaded]);

  if (isLoading) {
    return (
      <FlexBox height="100%" justifyContent="center" alignItems="center">
        <Spinner />
      </FlexBox>
    );
  }

  return (
    <Page.Section
      title={languageName}
      controls={
        <Button disabled={mode === 'edit' && pristine} onClick={handleSubmit}>
          Save customization
        </Button>
      }
    >
      {errorMessage && (
        <Box margin="0 0 400">
          <Banner
            variant="fail"
            icon={null}
            description={<>{errorMessage}</>}
          />
        </Box>
      )}

      <Form
        onSubmit={handleSubmit((data) => {
          setPreviewLoaded(false);
          onSubmit(data);
        })}
      >
        <Columns>
          <Column size="50%" className={styles.leftPane}>
            {!previewLoaded && <Spinner className={styles.previewSpinner} />}

            <ErrorBoundary fallback={<div>Something went wrong...</div>}>
              <CsatPreview
                ref={previewIframeRef}
                onLoad={handlePreviewLoaded}
                className={!previewLoaded ? styles.leftPaneLoading : undefined}
              />
            </ErrorBoundary>
          </Column>

          <Column size="50%" className={styles.rightPane}>
            <CustomizationFormView />
          </Column>
        </Columns>
      </Form>
    </Page.Section>
  );
};

export const CustomizationPageForm = reduxForm<
  LanguageCustomizationForm.FormValues,
  CustomizationPageViewProps
>({
  form: DixaMessengerPanelForms.LanguageCustomizationPageForm,
  enableReinitialize: true,
})(CustomizationPageView);

export const CustomizationPage = connect<
  StateProps,
  DispatchProps,
  OwnProps,
  CustomizationPageSelectors.State
>(
  (state, ownProps): StateProps => ({
    isLoading:
      state.settings.satisfaction.customization.messenger.languagesLevel
        .asyncStatus === 'loading',
    formValues:
      CustomizationPageSelectors.dixaMessengerLanguageCustomizationFormValuesgetter(
        state,
      ),
    initialValues:
      CustomizationPageSelectors.initialDixaMessengerValuesSelector(
        state,
        ownProps,
      ),
  }),
)(CustomizationPageForm);
